import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, X, Users, ChevronRight } from 'lucide-react';
import { SignedWaiversPanel } from '@/components/admin/SignedWaiversPanel';
import RegistrationDetailDrawer from '@/pages/admin-dashboard/RegistrationDetailDrawer';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const TIER_LABEL: Record<string, string> = {
  showcase: 'Showcase',
  club_priority: 'Club Priority',
  priority: 'Priority',
  standard: 'Standard',
};
const TIER_ORDER: Record<string, number> = { showcase: 1, club_priority: 2, priority: 3, standard: 4 };

const fmtMoney = (n: number) => `$${Number(n ?? 0).toFixed(2)}`;
const fmtDateTime = (s: string | null) => (s ? new Date(s).toLocaleString() : '');

export default function OperatorEventRegistrationsPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const tier = searchParams.get('tier');
  const division = searchParams.get('division');
  const paymentStatus = searchParams.get('payment_status');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [selectedRegId, setSelectedRegId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!eventId) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data: res, error: fnError } = await invokeEdgeFunction(
          'rle-list-event-registrations',
          { body: { event_id: eventId, tier, division, payment_status: paymentStatus } }
        );
        if (fnError || !res?.ok) {
          setError(res?.error?.message || fnError?.message || 'Could not load registrations.');
        } else {
          setData(res.data);
        }
      } catch (e: any) {
        setError(e?.message ?? 'Unexpected error');
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, tier, division, paymentStatus, reloadKey]);

  const activeFilters: Array<{ key: string; label: string }> = useMemo(() => {
    const f: Array<{ key: string; label: string }> = [];
    if (tier) f.push({ key: 'tier', label: TIER_LABEL[tier] ?? tier });
    if (division) f.push({ key: 'division', label: division });
    if (paymentStatus) f.push({ key: 'payment_status', label: paymentStatus.replace('_', ' ') });
    return f;
  }, [tier, division, paymentStatus]);

  const clearFilter = (key: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  // Group by tier for display (operators tend to think tier-first)
  const byTier = useMemo(() => {
    if (!data?.registrations) return [] as Array<{ tier: string; rows: any[] }>;
    const buckets: Record<string, any[]> = {};
    for (const r of data.registrations) {
      const t = (r.scheduling_tier ?? 'standard').toLowerCase();
      (buckets[t] ||= []).push(r);
    }
    return Object.entries(buckets)
      .map(([t, rows]) => ({ tier: t, rows }))
      .sort((a, b) => (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99));
  }, [data?.registrations]);

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      </div>
    );
  }

  if (!data) return null;

  const totalCount = data.registrations?.length ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate(`/admin-dashboard/events/${eventId}`)}
          className="flex items-center text-sm text-slate-600 hover:text-slate-900 mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Event overview
        </button>
        <h1 className="text-2xl font-bold tracking-tight">Registrations</h1>
        <p className="text-sm text-slate-500">{data.event?.name}</p>
      </div>

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex items-center flex-wrap gap-2">
          <span className="text-sm text-slate-500">Filters:</span>
          {activeFilters.map((f) => (
            <span
              key={f.key}
              className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700"
            >
              {f.label}
              <button
                onClick={() => clearFilter(f.key)}
                className="ml-1 inline-flex items-center justify-center rounded-full p-0.5 hover:bg-slate-300"
                aria-label={`Remove ${f.label} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <Button variant="ghost" size="sm" onClick={clearAll} className="h-7 text-xs">
            Clear all
          </Button>
        </div>
      )}

      {/* Count */}
      <div className="text-sm text-slate-500">
        {totalCount} {totalCount === 1 ? 'registration' : 'registrations'}
        {activeFilters.length > 0 && ' matching filters'}
      </div>

      <SignedWaiversPanel eventId={eventId} />

      {/* Empty state */}
      {totalCount === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-2 py-12 text-center text-slate-500">
            <Users className="w-8 h-8 opacity-50" />
            {activeFilters.length > 0
              ? 'No registrations match these filters.'
              : 'No registrations yet.'}
          </CardContent>
        </Card>
      )}

      {/* Grouped by tier */}
      {byTier.map(({ tier: t, rows }) => (
        <div key={t} className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {TIER_LABEL[t] ?? t} · {rows.length}
          </div>
          <div className="space-y-2">
            {rows.map((r: any) => (
              <button
                key={r.id}
                onClick={() => setSelectedRegId(r.id)}
                className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-md border bg-white hover:bg-slate-50 transition"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{r.team_name}</span>
                    {r.home_state && <span className="text-xs text-slate-500">({r.home_state})</span>}
                    <span className="text-xs text-slate-500">{r.division || 'no division'}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Roster: {r.roster_size}
                    {r.constraint_count > 0 && (
                      <> · {r.constraint_count} request{r.constraint_count === 1 ? '' : 's'}</>
                    )}{' '}
                    · {fmtDateTime(r.registered_at)}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">
                      {fmtMoney(r.total_amount_paid)}
                      {r.balance > 0 && (
                        <span className="text-slate-400"> / {fmtMoney(r.total_amount_due)}</span>
                      )}
                    </p>
                    <Badge variant="secondary" className="mt-0.5 text-[10px]">
                      {(r.payment_status || 'unknown').replace('_', ' ')}
                    </Badge>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}

      <RegistrationDetailDrawer
        registrationId={selectedRegId}
        eventId={eventId as string}
        onClose={() => setSelectedRegId(null)}
        levelsEnabled={false}
        levels={[]}
        invalidateList={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}
